import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Ban, Check, Copy, Mail, RotateCcw, Send, Store, Trash2, TriangleAlert } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Vendor invites — the maker's side of the invite tracker.
 *
 * Mirrors the practitioner tab because the owner asked for the same thing for vendors, with the
 * stages that actually apply to a storefront: agreements, application, approval, first product.
 */
const STAGE_STYLE = {
  invited:            'bg-slate-500/15 text-slate-200 border-slate-400/30',
  expired:            'bg-amber-500/15 text-amber-200 border-amber-500/30',
  revoked:            'bg-rose-500/15 text-rose-200 border-rose-500/30',
  claimed:            'bg-sky-500/15 text-sky-200 border-sky-500/30',
  legal_accepted:     'bg-indigo-500/15 text-indigo-200 border-indigo-500/30',
  agreements_signed:  'bg-violet-500/15 text-violet-200 border-violet-500/30',
  applied:            'bg-cyan-500/15 text-cyan-200 border-cyan-500/30',
  approved:           'bg-teal-500/15 text-teal-200 border-teal-500/30',
  complete:           'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
  rejected:           'bg-rose-500/15 text-rose-200 border-rose-500/30',
  deleted:            'bg-gray-500/15 text-gray-300 border-gray-500/30',
};

const parsePasted = (text) =>
  (text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const angle = line.match(/^(.*?)[<(]\s*([^\s>)]+@[^\s>)]+)\s*[>)]?$/);
      if (angle) return { full_name: angle[1].trim().replace(/[,;]$/, ''), email: angle[2].toLowerCase() };
      const parts = line.split(/[,;\t]|\s{2,}/).map((p) => p.trim()).filter(Boolean);
      const email = parts.find((p) => p.includes('@'));
      const name = parts.filter((p) => p !== email).join(' ').trim();
      return { full_name: name || (email || '').split('@')[0], email: (email || '').toLowerCase(), invalid: !email };
    });

const InviteLink = ({ link }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button size="sm" variant="ghost" className="h-8 shrink-0 text-gray-300 hover:text-white"
      onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600); }}
      data-testid="copy-vendor-invite-link-btn">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
};

export default function AdminVendorInvitesTab() {
  const [data, setData] = useState(null);
  const [pasted, setPasted] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/vendors/invites`, auth());
      setData(r.data);
    } catch {
      toast.error('Could not load vendor invites');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const parsed = parsePasted(pasted);
  const valid = parsed.filter((p) => !p.invalid && p.email);
  const isPreview = (data?.frontend_url || '').includes('.preview.emergentagent.com');

  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/vendors/invites`,
        { invites: valid.map(({ full_name, email }) => ({ full_name, email })), send_email: sendEmail },
        auth());
      const n = r.data.created.length;
      toast.success(n ? `${n} vendor account${n > 1 ? 's' : ''} created` : 'Nothing created');
      (r.data.skipped || []).forEach((s) => toast.error(`${s.email}: ${s.reason}`));
      setFresh(r.data.created || []);
      setPasted('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the invites');
    } finally {
      setBusy(false);
    }
  };

  const act = async (id, what) => {
    try {
      if (what === 'delete') {
        await axios.delete(`${API}/admin/vendors/invites/${id}`, auth());
        toast.success('Invite and unclaimed account removed');
      } else {
        const r = await axios.post(`${API}/admin/vendors/invites/${id}/${what}`, {}, auth());
        toast.success(what === 'resend'
          ? (r.data.emailed ? 'A fresh link is on its way' : 'New link created — email did not send, copy it below')
          : 'Invite revoked');
        if (what === 'resend' && r.data.invite_link) {
          setFresh([{ id, email: '', full_name: '', invite_link: r.data.invite_link, emailed: r.data.emailed }]);
        }
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not work');
    }
  };

  if (!data) return <div className="py-16 text-center text-gray-400">Loading vendors…</div>;

  return (
    <div className="space-y-5" data-testid="admin-vendor-invites-tab">
      {isPreview && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-4 flex gap-3"
          data-testid="vendor-preview-url-warning">
          <TriangleAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-100/90 leading-relaxed">
            <strong className="text-amber-200">Don&apos;t send real invites from here yet.</strong>{' '}
            Invite links are built from this environment&apos;s address
            (<code className="text-amber-200/80">{data.frontend_url}</code>), which sleeps after 30
            minutes and changes when the workspace is forked. Publish first, then send.
          </div>
        </div>
      )}

      <Card className="bg-slate-900/70 border-white/10">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Store className="w-4 h-4 text-amber-300" />
            <h3 className="text-white font-medium">Invite vendors</h3>
          </div>
          <p className="text-sm text-gray-400 mb-4 max-w-3xl">
            One per line — <span className="text-gray-300">Jane Doe, jane@example.com</span>. Each
            gets a <strong className="text-gray-300">Bronze</strong> account with no password and a
            single-use link that lasts {data.invite_days} days. They pay nothing for a storefront —
            the platform takes its percentage of each sale — and they still complete the store
            application, which is where they agree to the Vendor Terms and Marketplace Guidelines.
          </p>

          <Textarea
            rows={5}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={'Jane Doe, jane@example.com\nSam Rivers <sam@example.com>'}
            className="bg-slate-950/60 border-white/15 text-white font-mono text-sm"
            data-testid="vendor-invite-paste-box"
          />

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="accent-amber-400"
                data-testid="vendor-invite-send-email-toggle"
              />
              Email the invitation now
            </label>

            {parsed.length > 0 && (
              <span className="text-sm text-gray-400" data-testid="vendor-invite-parsed-count">
                {valid.length} ready
                {parsed.length !== valid.length && (
                  <span className="text-rose-300"> · {parsed.length - valid.length} without an email</span>
                )}
              </span>
            )}

            <Button onClick={send} disabled={busy || !valid.length}
              className="ml-auto" data-testid="vendor-invite-send-btn">
              <Send className="w-4 h-4 mr-2" />
              {busy ? 'Creating…' : sendEmail ? `Create & invite ${valid.length || ''}`.trim()
                                              : `Create ${valid.length || ''} without email`.trim()}
            </Button>
          </div>
        </CardContent>
      </Card>

      {fresh.length > 0 && (
        <Card className="bg-slate-900/70 border-amber-500/25">
          <CardContent className="p-5">
            <h3 className="text-white font-medium mb-1">Invitation links</h3>
            <p className="text-sm text-gray-400 mb-4">
              Shown once. Copy any you need to pass on by hand — they aren&apos;t stored anywhere in
              readable form.
            </p>
            <div className="space-y-2">
              {fresh.map((c) => (
                <div key={c.id} className="flex items-center gap-3 text-sm">
                  <span className="text-white w-48 truncate">{c.full_name || c.email || 'New link'}</span>
                  <span className="text-gray-500 flex-1 truncate font-mono text-xs">{c.invite_link}</span>
                  {c.emailed ? (
                    <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-500/30 gap-1 shrink-0">
                      <Mail className="w-3 h-3" />Emailed
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/15 text-amber-200 border-amber-500/30 shrink-0">
                      Not emailed
                    </Badge>
                  )}
                  <InviteLink link={c.invite_link} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-white/10">
            <h3 className="text-white font-medium">
              Vendors <span className="text-gray-500 font-normal">({data.invites.length})</span>
            </h3>
          </div>

          {data.invites.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-500 text-sm">
              No vendors invited yet.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {data.invites.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5"
                  data-testid={`vendor-invite-row-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">{inv.full_name}</div>
                    <div className="text-gray-500 text-xs truncate">{inv.email}</div>
                  </div>

                  <Badge className={`${STAGE_STYLE[inv.status.stage] || STAGE_STYLE.deleted} shrink-0`}
                    data-testid={`vendor-invite-stage-${inv.id}`}>
                    {inv.status.label}
                  </Badge>

                  <span className="text-xs text-gray-600 w-32 text-right shrink-0">
                    {inv.send_count > 0
                      ? `sent ${inv.send_count}×`
                      : inv.redeemed_at ? `via ${inv.redeemed_via}` : 'not emailed'}
                  </span>

                  <div className="flex items-center gap-1 shrink-0">
                    {!inv.redeemed_at && (
                      <Button size="sm" variant="ghost" onClick={() => act(inv.id, 'resend')}
                        className="h-8 text-gray-300 hover:text-white hover:bg-white/10"
                        title="Rotate the token and email a fresh link"
                        data-testid={`vendor-invite-resend-${inv.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {!inv.redeemed_at && !inv.revoked_at && (
                      <Button size="sm" variant="ghost" onClick={() => act(inv.id, 'revoke')}
                        className="h-8 text-gray-300 hover:text-rose-300 hover:bg-rose-500/10"
                        title="Kill the link, keep the account"
                        data-testid={`vendor-invite-revoke-${inv.id}`}>
                        <Ban className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {!inv.redeemed_at && (
                      <Button size="sm" variant="ghost" onClick={() => act(inv.id, 'delete')}
                        className="h-8 text-gray-300 hover:text-rose-300 hover:bg-rose-500/10"
                        title="Remove the invite and the unclaimed account"
                        data-testid={`vendor-invite-delete-${inv.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
